// ============================================
// FULL-FEATURED ROI CALCULATOR
// Mobile-optimized with unit selection & detailed breakdown
// With German/English translation support
// + Corporate Structuring Recommendation & Calendly Booking
// ============================================

import React, { useState, useMemo } from 'react';
import { BarChart3, Lightbulb, HelpCircle, Home } from 'lucide-react';
import { useLanguage } from './LanguageContext';

interface ROICalculatorViewProps {
  currentInvestor: any;
  units: any[];
}

// Fee constants (Dubai standard)
const DLD_FEE_PERCENT = 4;
const BROKER_FEE_PERCENT = 2;

// Structuring thresholds (in AED)
const STRUCTURING_THRESHOLDS = {
  notWorthIt: 50000,      // Below this: not worth it
  consider: 100000,       // Consider structuring
  recommended: 250000,    // Recommended
  highlyRecommended: 500000  // Highly recommended
};



const ROICalculatorView: React.FC<ROICalculatorViewProps> = ({ currentInvestor, units = [] }) => {
  const { t } = useLanguage();
  
  const [appreciation, setAppreciation] = useState(25);
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [paidPercent, setPaidPercent] = useState(55);
  const [showFAQ, setShowFAQ] = useState(false);

  // Safe parsing function
  const parsePrice = (value: any): number => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[AED,\s]/gi, '').trim();
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  // Safely get units array and filter valid ones
  const safeUnits = Array.isArray(units) ? units : [];
  
  const validUnits = useMemo(() => {
    return safeUnits.filter(unit => {
      const price = parsePrice(unit?.currentValue) || parsePrice(unit?.purchaseValue) || parsePrice(unit?.value) || 0;
      return price > 0;
    });
  }, [safeUnits]);

  // Calculations for each unit
  const calculations = useMemo(() => {
    return validUnits.map(unit => {
      const originalPrice = parsePrice(unit.purchaseValue) || parsePrice(unit.currentValue) || parsePrice(unit.value) || 0;
      const sellingPrice = originalPrice * (1 + appreciation / 100);
      const paidAmount = originalPrice * (paidPercent / 100);
      const remainingBalance = originalPrice - paidAmount;
      const netToSeller = sellingPrice - remainingBalance;
      const dldFee = sellingPrice * (DLD_FEE_PERCENT / 100);
      const brokerFee = sellingPrice * (BROKER_FEE_PERCENT / 100);
      const totalFees = dldFee + brokerFee;
      const grossNet = netToSeller - totalFees;
      const profit = grossNet - paidAmount;
      const roi = originalPrice > 0 ? (profit / originalPrice) * 100 : 0;
      const roe = paidAmount > 0 ? (profit / paidAmount) * 100 : 0;

      return {
        ...unit, originalPrice, sellingPrice, paidAmount, remainingBalance,
        netToSeller, dldFee, brokerFee, totalFees, grossNet, profit, roi, roe
      };
    });
  }, [validUnits, appreciation, paidPercent]);

  const filteredCalculations = useMemo(() => {
    if (selectedUnit === 'all') return calculations;
    return calculations.filter(calc => calc.id === selectedUnit);
  }, [calculations, selectedUnit]);

  const totals = useMemo(() => {
    const data = filteredCalculations;
    const totalOriginal = data.reduce((sum, c) => sum + c.originalPrice, 0);
    const totalSelling = data.reduce((sum, c) => sum + c.sellingPrice, 0);
    const totalPaid = data.reduce((sum, c) => sum + c.paidAmount, 0);
    const totalDldFee = data.reduce((sum, c) => sum + c.dldFee, 0);
    const totalBrokerFee = data.reduce((sum, c) => sum + c.brokerFee, 0);
    const totalGrossNet = data.reduce((sum, c) => sum + c.grossNet, 0);
    const totalProfit = data.reduce((sum, c) => sum + c.profit, 0);
    const overallROI = totalOriginal > 0 ? (totalProfit / totalOriginal) * 100 : 0;
    const overallROE = totalPaid > 0 ? (totalProfit / totalPaid) * 100 : 0;

    return {
      totalOriginal, totalSelling, totalPaid, totalDldFee, totalBrokerFee,
      totalGrossNet, totalProfit, overallROI, overallROE, unitCount: data.length
    };
  }, [filteredCalculations]);

  // Determine structuring recommendation based on profit
  const structuringStatus = useMemo(() => {
    const profit = totals.totalProfit;
    
    if (profit >= STRUCTURING_THRESHOLDS.highlyRecommended) {
      return {
        level: 'highlyRecommended',
        color: '#22c55e',
        bgColor: 'rgba(34, 197, 94, 0.1)',
        borderColor: '#22c55e',
        icon: <Home size={24} />,
        title: t('structuringHighlyRecommended'),
        description: t('structuringHighlyRecommendedDesc'),
        showButton: true,
        urgency: 4
      };
    } else if (profit >= STRUCTURING_THRESHOLDS.recommended) {
      return {
        level: 'recommended',
        color: '#D4AF37',
        bgColor: 'rgba(212, 175, 55, 0.1)',
        borderColor: '#D4AF37',
        icon: <BarChart3 size={24} />,
        title: t('structuringRecommended'),
        description: t('structuringRecommendedDesc'),
        showButton: true,
        urgency: 3
      };
    } else if (profit >= STRUCTURING_THRESHOLDS.consider) {
      return {
        level: 'consider',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.1)',
        borderColor: '#f59e0b',
        icon: <Lightbulb size={24} />,
        title: t('considerStructuring'),
        description: t('considerStructuringDesc'),
        showButton: true,
        urgency: 2
      };
    } else {
      return {
        level: 'notNeeded',
        color: '#6b7280',
        bgColor: 'rgba(107, 114, 128, 0.08)',
        borderColor: '#e5e5e5',
        icon: <BarChart3 size={24} />,
        title: t('structuringNotNeeded'),
        description: t('structuringNotNeededDesc'),
        showButton: false,
        urgency: 1
      };
    }
  }, [totals.totalProfit, t]);

  // Calculate potential savings (rough estimate: 20-35% of profit depending on home country tax rate)
  const potentialSavings = useMemo(() => {
    const profit = totals.totalProfit;
    // Assuming German investor with ~30% effective tax rate on capital gains
    const estimatedTaxRate = 0.30;
    return profit * estimatedTaxRate;
  }, [totals.totalProfit]);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCurrencyShort = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toFixed(0);
  };

  // Open Calendly link
  const openCalendly = () => {
    window.open('https://calendly.com/j-kijasko-investmenter/internationale-steuerberatung-investmenter-app', '_blank');
  };

  // Styles
  const styles = {
    container: { padding: '16px', paddingBottom: '100px', fontFamily: "'Montserrat', -apple-system, sans-serif", background: '#f5f5f5', minHeight: '100vh' } as React.CSSProperties,
    header: { marginBottom: '20px', paddingBottom: '15px', borderBottom: '3px solid #D4AF37' } as React.CSSProperties,
    title: { fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', margin: '0 0 5px 0', display: 'flex', alignItems: 'center', gap: '10px' } as React.CSSProperties,
    subtitle: { color: '#666', fontSize: '0.85rem', margin: 0 } as React.CSSProperties,
    controlsSection: { background: '#fff', borderRadius: '12px', padding: '16px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' } as React.CSSProperties,
    controlRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' } as React.CSSProperties,
    controlLabel: { fontSize: '0.75rem', fontWeight: '600', color: '#1a1a2e', textTransform: 'uppercase' as const, letterSpacing: '0.5px', minWidth: '100px' } as React.CSSProperties,
    select: { flex: 1, background: '#D4AF37', border: 'none', color: '#000', padding: '12px 16px', fontSize: '0.9rem', fontWeight: '600', borderRadius: '8px', cursor: 'pointer', appearance: 'none' as const, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '36px' } as React.CSSProperties,
    summarySection: { marginBottom: '16px' } as React.CSSProperties,
    sectionTitle: { fontSize: '0.8rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '12px', textTransform: 'uppercase' as const, letterSpacing: '1px' } as React.CSSProperties,
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' } as React.CSSProperties,
    summaryCard: { background: '#fff', padding: '14px', borderRadius: '10px', border: '2px solid #e5e5e5' } as React.CSSProperties,
    summaryCardDark: { background: '#1a1a2e', padding: '14px', borderRadius: '10px', border: '2px solid #D4AF37' } as React.CSSProperties,
    cardLabel: { fontSize: '0.6rem', fontWeight: '600', color: '#888', textTransform: 'uppercase' as const, letterSpacing: '0.3px', marginBottom: '4px' } as React.CSSProperties,
    cardLabelLight: { fontSize: '0.6rem', fontWeight: '600', color: '#aaa', textTransform: 'uppercase' as const, letterSpacing: '0.3px', marginBottom: '4px' } as React.CSSProperties,
    cardValue: { fontSize: '1rem', fontWeight: '700', color: '#1a1a2e', lineHeight: 1.2 } as React.CSSProperties,
    cardValueGold: { fontSize: '1.2rem', fontWeight: '700', color: '#D4AF37', lineHeight: 1.2 } as React.CSSProperties,
    cardValueGreen: { fontSize: '1rem', fontWeight: '700', color: '#22c55e', lineHeight: 1.2 } as React.CSSProperties,
    cardMeta: { fontSize: '0.6rem', color: '#999', marginTop: '2px' } as React.CSSProperties,
    cardMetaLight: { fontSize: '0.6rem', color: '#777', marginTop: '2px' } as React.CSSProperties,
    breakdownSection: { background: '#fff', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '16px' } as React.CSSProperties,
    breakdownTitle: { fontSize: '0.85rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '16px', paddingBottom: '10px', borderBottom: '2px solid #D4AF37', textTransform: 'uppercase' as const, letterSpacing: '0.5px' } as React.CSSProperties,
    unitCard: { background: '#fafafa', borderRadius: '10px', padding: '14px', marginBottom: '12px', border: '1px solid #eee' } as React.CSSProperties,
    unitHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #e5e5e5' } as React.CSSProperties,
    unitName: { fontSize: '0.95rem', fontWeight: '700', color: '#1a1a2e', margin: 0 } as React.CSSProperties,
    unitMeta: { fontSize: '0.75rem', color: '#666', margin: '2px 0 0 0' } as React.CSSProperties,
    roiBadges: { display: 'flex', gap: '6px' } as React.CSSProperties,
    badge: { padding: '4px 10px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '700' } as React.CSSProperties,
    badgeGold: { background: '#D4AF37', color: '#000' } as React.CSSProperties,
    badgeGreen: { background: '#22c55e', color: '#fff' } as React.CSSProperties,
    unitGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' } as React.CSSProperties,
    unitStat: { padding: '8px', background: '#fff', borderRadius: '6px' } as React.CSSProperties,
    unitStatLabel: { fontSize: '0.55rem', fontWeight: '600', color: '#888', textTransform: 'uppercase' as const, marginBottom: '2px' } as React.CSSProperties,
    unitStatValue: { fontSize: '0.8rem', fontWeight: '600', color: '#1a1a2e' } as React.CSSProperties,
    unitStatValueGreen: { fontSize: '0.8rem', fontWeight: '700', color: '#22c55e' } as React.CSSProperties,
    unitStatValueRed: { fontSize: '0.8rem', fontWeight: '600', color: '#ef4444' } as React.CSSProperties,
    totalRow: { background: '#1a1a2e', borderRadius: '10px', padding: '16px', marginTop: '8px' } as React.CSSProperties,
    totalGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' } as React.CSSProperties,
    totalLabel: { fontSize: '0.6rem', fontWeight: '600', color: '#aaa', textTransform: 'uppercase' as const, marginBottom: '2px' } as React.CSSProperties,
    totalValue: { fontSize: '0.95rem', fontWeight: '700', color: '#fff' } as React.CSSProperties,
    totalValueGold: { fontSize: '1.1rem', fontWeight: '700', color: '#D4AF37' } as React.CSSProperties,
    faqButton: { width: '100%', background: '#1a1a2e', border: 'none', color: '#fff', padding: '14px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' } as React.CSSProperties,
    emptyState: { textAlign: 'center' as const, padding: '40px 20px', background: '#fff', borderRadius: '12px' } as React.CSSProperties,
    modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' } as React.CSSProperties,
    modalContent: { background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxHeight: '80vh', overflow: 'hidden' } as React.CSSProperties,
    modalHeader: { background: '#1a1a2e', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } as React.CSSProperties,
    modalTitle: { fontSize: '1rem', fontWeight: '700', margin: 0 } as React.CSSProperties,
    modalClose: { background: '#D4AF37', border: 'none', color: '#000', width: '32px', height: '32px', borderRadius: '8px', fontSize: '1.2rem', cursor: 'pointer', fontWeight: '700' } as React.CSSProperties,
    modalBody: { padding: '20px', overflowY: 'auto' as const, maxHeight: 'calc(80vh - 60px)' } as React.CSSProperties,
    faqItem: { marginBottom: '16px', padding: '14px', background: '#f8f9fa', borderRadius: '10px', borderLeft: '4px solid #D4AF37' } as React.CSSProperties,
    faqQuestion: { fontSize: '0.9rem', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px 0' } as React.CSSProperties,
    faqAnswer: { fontSize: '0.8rem', color: '#555', lineHeight: 1.6, margin: 0 } as React.CSSProperties,
    formula: { background: '#1a1a2e', color: '#D4AF37', padding: '8px 12px', margin: '8px 0 0 0', fontFamily: 'monospace', fontSize: '0.75rem', borderRadius: '6px' } as React.CSSProperties,
    // New structuring section styles
    structuringSection: { 
      background: '#fff', 
      borderRadius: '12px', 
      padding: '16px', 
      marginBottom: '16px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      border: '2px solid'
    } as React.CSSProperties,
    structuringHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #e5e5e5'
    } as React.CSSProperties,
    structuringIcon: {
      fontSize: '2rem',
      lineHeight: 1
    } as React.CSSProperties,
    structuringTitleWrap: {
      flex: 1
    } as React.CSSProperties,
    structuringTitle: {
      fontSize: '1rem',
      fontWeight: '700',
      margin: '0 0 2px 0'
    } as React.CSSProperties,
    structuringSubtitle: {
      fontSize: '0.7rem',
      color: '#888',
      margin: 0,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    } as React.CSSProperties,
    statusBadge: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.65rem',
      fontWeight: '700',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    } as React.CSSProperties,
    structuringBody: {
      marginBottom: '16px'
    } as React.CSSProperties,
    structuringDescription: {
      fontSize: '0.85rem',
      color: '#555',
      lineHeight: 1.6,
      margin: '0 0 16px 0'
    } as React.CSSProperties,
    metricsRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
      marginBottom: '16px'
    } as React.CSSProperties,
    metricCard: {
      padding: '12px',
      borderRadius: '8px',
      background: '#f8f9fa'
    } as React.CSSProperties,
    metricLabel: {
      fontSize: '0.6rem',
      fontWeight: '600',
      color: '#888',
      textTransform: 'uppercase' as const,
      marginBottom: '4px'
    } as React.CSSProperties,
    metricValue: {
      fontSize: '1.1rem',
      fontWeight: '700'
    } as React.CSSProperties,
    thresholdIndicator: {
      background: '#f8f9fa',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '16px'
    } as React.CSSProperties,
    thresholdBar: {
      height: '8px',
      background: '#e5e5e5',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '8px',
      position: 'relative' as const
    } as React.CSSProperties,
    thresholdFill: {
      height: '100%',
      borderRadius: '4px',
      transition: 'width 0.5s ease'
    } as React.CSSProperties,
    thresholdLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.6rem',
      color: '#888'
    } as React.CSSProperties,
    thresholdText: {
      fontSize: '0.75rem',
      color: '#666',
      textAlign: 'center' as const,
      marginTop: '8px'
    } as React.CSSProperties,
    bookingButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #D4AF37 0%, #f4cf67 50%, #D4AF37 100%)',
      border: 'none',
      color: '#000',
      padding: '16px 20px',
      fontSize: '0.95rem',
      fontWeight: '700',
      cursor: 'pointer',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    } as React.CSSProperties,
    bookingButtonSecondary: {
      width: '100%',
      background: '#fff',
      border: '2px solid #D4AF37',
      color: '#1a1a2e',
      padding: '14px 20px',
      fontSize: '0.85rem',
      fontWeight: '600',
      cursor: 'pointer',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      marginTop: '10px'
    } as React.CSSProperties,
    bookingMeta: {
      fontSize: '0.7rem',
      color: '#666',
      textAlign: 'center' as const,
      marginTop: '10px'
    } as React.CSSProperties,
    savingsCard: {
      background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d4a 100%)',
      borderRadius: '10px',
      padding: '14px',
      marginBottom: '16px'
    } as React.CSSProperties,
    savingsLabel: {
      fontSize: '0.6rem',
      fontWeight: '600',
      color: '#aaa',
      textTransform: 'uppercase' as const,
      marginBottom: '4px'
    } as React.CSSProperties,
    savingsValue: {
      fontSize: '1.4rem',
      fontWeight: '700',
      color: '#22c55e'
    } as React.CSSProperties,
    savingsDisclaimer: {
      fontSize: '0.6rem',
      color: '#777',
      marginTop: '6px',
      lineHeight: 1.4
    } as React.CSSProperties,
  };

  // Calculate threshold progress
  const thresholdProgress = useMemo(() => {
    const profit = totals.totalProfit;
    const maxThreshold = STRUCTURING_THRESHOLDS.highlyRecommended;
    return Math.min((profit / maxThreshold) * 100, 100);
  }, [totals.totalProfit]);

  // Empty state
  if (validUnits.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}><BarChart3 size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />{t('roiCalculator')}</h1>
          <p style={styles.subtitle}>{t('investmentReturnAnalysis')}</p>
        </div>
        <div style={styles.emptyState}>
          <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'center' }}><Home size={48} color="#999" /></div>
          <h2 style={{ fontSize: '1.2rem', color: '#1a1a2e', marginBottom: '10px' }}>{t('noPropertiesFound')}</h2>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>{t('addPropertiesToCalculate')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}><BarChart3 size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />{t('roiCalculator')}</h1>
        <p style={styles.subtitle}>{currentInvestor?.name} - {t('investmentAnalysis')}</p>
      </div>

      {/* Controls */}
      <div style={styles.controlsSection}>
        <div style={styles.controlRow}>
          <span style={styles.controlLabel}>{t('appreciation')}</span>
          <select style={styles.select} value={appreciation} onChange={(e) => setAppreciation(Number(e.target.value))}>
            <option value={10}>10%</option>
            <option value={15}>15%</option>
            <option value={20}>20%</option>
            <option value={25}>25%</option>
            <option value={30}>30%</option>
            <option value={35}>35%</option>
            <option value={40}>40%</option>
          </select>
        </div>
        
        <div style={styles.controlRow}>
          <span style={styles.controlLabel}>{t('unit')}</span>
          <select style={styles.select} value={selectedUnit} onChange={(e) => setSelectedUnit(e.target.value)}>
            <option value="all">{t('allUnits')} ({validUnits.length})</option>
            {validUnits.map((unit) => (
              <option key={unit.id} value={unit.id}>{unit.name} {unit.unitNumber ? `(${unit.unitNumber})` : ''}</option>
            ))}
          </select>
        </div>
        
        <div style={{...styles.controlRow, marginBottom: 0}}>
          <span style={styles.controlLabel}>{t('paidPercent')}</span>
          <select style={styles.select} value={paidPercent} onChange={(e) => setPaidPercent(Number(e.target.value))}>
            <option value={30}>30%</option>
            <option value={40}>40%</option>
            <option value={50}>50%</option>
            <option value={55}>55%</option>
            <option value={60}>60%</option>
            <option value={70}>70%</option>
            <option value={80}>80%</option>
            <option value={100}>100% ({t('fullyPaid')})</option>
          </select>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div style={styles.summarySection}>
        <div style={styles.sectionTitle}>{t('portfolioSummary')}</div>
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>{t('units')}</div>
            <div style={styles.cardValue}>{totals.unitCount}</div>
            <div style={styles.cardMeta}>{t('properties')}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>{t('originalValue')}</div>
            <div style={styles.cardValue}>{formatCurrency(totals.totalOriginal)}</div>
            <div style={styles.cardMeta}>AED</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>{t('sellingPrice')} ({appreciation}%)</div>
            <div style={styles.cardValue}>{formatCurrency(totals.totalSelling)}</div>
            <div style={styles.cardMeta}>AED</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>{t('paidInstallment')} ({paidPercent}%)</div>
            <div style={styles.cardValue}>{formatCurrency(totals.totalPaid)}</div>
            <div style={styles.cardMeta}>AED</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>{t('grossNet')}</div>
            <div style={styles.cardValue}>{formatCurrency(totals.totalGrossNet)}</div>
            <div style={styles.cardMeta}>AED</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>{t('totalProfit')}</div>
            <div style={styles.cardValueGreen}>{formatCurrency(totals.totalProfit)}</div>
            <div style={styles.cardMeta}>AED</div>
          </div>
          <div style={styles.summaryCardDark}>
            <div style={styles.cardLabelLight}>ROI</div>
            <div style={styles.cardValueGold}>{totals.overallROI.toFixed(2)}%</div>
            <div style={styles.cardMetaLight}>{t('profitDividedByInvestment')}</div>
          </div>
          <div style={styles.summaryCardDark}>
            <div style={styles.cardLabelLight}>ROE</div>
            <div style={styles.cardValueGold}>{totals.overallROE.toFixed(2)}%</div>
            <div style={styles.cardMetaLight}>{t('profitDividedByEquity')} ({paidPercent}%)</div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* NEW: Corporate Structuring Recommendation */}
      {/* ============================================ */}
      <div style={{
        ...styles.structuringSection,
        borderColor: structuringStatus.borderColor,
        background: structuringStatus.bgColor
      }}>
        {/* Header */}
        <div style={styles.structuringHeader}>
          <div style={styles.structuringIcon}>{structuringStatus.icon}</div>
          <div style={styles.structuringTitleWrap}>
            <h3 style={{...styles.structuringTitle, color: structuringStatus.color}}>
              {structuringStatus.title}
            </h3>
            <p style={styles.structuringSubtitle}>{t('structuringRecommendation')}</p>
          </div>
          <span style={{
            ...styles.statusBadge,
            background: structuringStatus.color,
            color: structuringStatus.level === 'notNeeded' ? '#fff' : '#000'
          }}>
            {structuringStatus.level === 'notNeeded' ? t('belowThreshold') : t('thresholdReached')}
          </span>
        </div>

        {/* Body */}
        <div style={styles.structuringBody}>
          <p style={styles.structuringDescription}>{structuringStatus.description}</p>

          {/* Metrics Row */}
          <div style={styles.metricsRow}>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>{t('yourProjectedProfit')}</div>
              <div style={{...styles.metricValue, color: totals.totalProfit > 0 ? '#22c55e' : '#1a1a2e'}}>
                {formatCurrencyShort(totals.totalProfit)} AED
              </div>
            </div>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>ROE</div>
              <div style={{...styles.metricValue, color: '#D4AF37'}}>
                {totals.overallROE.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Threshold Progress Bar */}
          <div style={styles.thresholdIndicator}>
            <div style={styles.thresholdBar}>
              <div style={{
                ...styles.thresholdFill,
                width: `${thresholdProgress}%`,
                background: `linear-gradient(90deg, #6b7280 0%, #f59e0b 33%, #D4AF37 66%, #22c55e 100%)`
              }} />
            </div>
            <div style={styles.thresholdLabels}>
              <span>0</span>
              <span>{formatCurrencyShort(STRUCTURING_THRESHOLDS.consider)}</span>
              <span>{formatCurrencyShort(STRUCTURING_THRESHOLDS.recommended)}</span>
              <span>{formatCurrencyShort(STRUCTURING_THRESHOLDS.highlyRecommended)}+</span>
            </div>
            <p style={styles.thresholdText}>
              <Lightbulb size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('structureMakesSenseFrom')} <strong>{formatCurrencyShort(STRUCTURING_THRESHOLDS.consider)} AED</strong> {t('profitOnwards')}
            </p>
          </div>

          {/* Potential Savings Card (only show if structuring is recommended) */}
          {structuringStatus.showButton && potentialSavings > 10000 && (
            <div style={styles.savingsCard}>
              <div style={styles.savingsLabel}>{t('potentialSavings')}</div>
              <div style={styles.savingsValue}>~{formatCurrencyShort(potentialSavings)} AED</div>
              <p style={styles.savingsDisclaimer}>{t('savingsDisclaimer')}</p>
            </div>
          )}
        </div>

        {/* Booking Button */}
        {structuringStatus.showButton ? (
          <>
            <button 
              style={styles.bookingButton}
              onClick={openCalendly}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.3)';
              }}
            >
              📅 {t('bookTaxConsultation')}
            </button>
            <p style={styles.bookingMeta}>
              ✓ {t('freeConsultation')} {t('withTaxExpert')}
            </p>
          </>
        ) : (
          <button 
            style={styles.bookingButtonSecondary}
            onClick={openCalendly}
          >
            💬 {t('bookConsultation')}
          </button>
        )}
      </div>
      {/* END: Corporate Structuring Recommendation */}

      {/* Unit Breakdown */}
      <div style={styles.breakdownSection}>
        <div style={styles.breakdownTitle}>{t('unitBreakdown')}</div>
        
        {filteredCalculations.map((calc) => (
          <div key={calc.id} style={styles.unitCard}>
            <div style={styles.unitHeader}>
              <div>
                <h3 style={styles.unitName}>{calc.name}</h3>
                <p style={styles.unitMeta}>{calc.unitNumber || ''} {calc.project ? `• ${calc.project}` : ''}</p>
              </div>
              <div style={styles.roiBadges}>
                <span style={{...styles.badge, ...styles.badgeGold}}>{calc.roi.toFixed(2)}%</span>
                <span style={{...styles.badge, ...styles.badgeGreen}}>{calc.roe.toFixed(2)}%</span>
              </div>
            </div>
            
            <div style={styles.unitGrid}>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('originalPrice')}</div>
                <div style={styles.unitStatValue}>{formatCurrency(calc.originalPrice)}</div>
              </div>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('sellingPrice')}</div>
                <div style={styles.unitStatValue}>{formatCurrency(calc.sellingPrice)}</div>
              </div>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('paid')} ({paidPercent}%)</div>
                <div style={styles.unitStatValue}>{formatCurrency(calc.paidAmount)}</div>
              </div>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('netToSeller')}</div>
                <div style={styles.unitStatValue}>{formatCurrency(calc.netToSeller)}</div>
              </div>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('dldFee')} 4%</div>
                <div style={styles.unitStatValueRed}>{formatCurrency(calc.dldFee)}</div>
              </div>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('brokerFee')} 2%</div>
                <div style={styles.unitStatValueRed}>{formatCurrency(calc.brokerFee)}</div>
              </div>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('grossNet')}</div>
                <div style={styles.unitStatValue}>{formatCurrency(calc.grossNet)}</div>
              </div>
              <div style={styles.unitStat}>
                <div style={styles.unitStatLabel}>{t('profit')}</div>
                <div style={styles.unitStatValueGreen}>{formatCurrency(calc.profit)}</div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredCalculations.length > 1 && (
          <div style={styles.totalRow}>
            <div style={styles.totalGrid}>
              <div><div style={styles.totalLabel}>{t('totalOriginal')}</div><div style={styles.totalValue}>{formatCurrency(totals.totalOriginal)}</div></div>
              <div><div style={styles.totalLabel}>{t('totalSelling')}</div><div style={styles.totalValue}>{formatCurrency(totals.totalSelling)}</div></div>
              <div><div style={styles.totalLabel}>{t('totalPaid')}</div><div style={styles.totalValue}>{formatCurrency(totals.totalPaid)}</div></div>
              <div><div style={styles.totalLabel}>{t('totalFees')}</div><div style={styles.totalValue}>{formatCurrency(totals.totalDldFee + totals.totalBrokerFee)}</div></div>
              <div><div style={styles.totalLabel}>{t('totalGrossNet')}</div><div style={styles.totalValue}>{formatCurrency(totals.totalGrossNet)}</div></div>
              <div><div style={styles.totalLabel}>{t('totalProfit')}</div><div style={styles.totalValueGold}>{formatCurrency(totals.totalProfit)}</div></div>
            </div>
          </div>
        )}
      </div>

      {/* FAQ Button */}
      <button style={styles.faqButton} onClick={() => setShowFAQ(true)}><HelpCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('howRoiCalculated')}</button>

      {/* FAQ Modal */}
      {showFAQ && (
        <div style={styles.modalOverlay} onClick={() => setShowFAQ(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{t('understandingRoiCalculator')}</h2>
              <button style={styles.modalClose} onClick={() => setShowFAQ(false)}>×</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.faqItem}>
                <h3 style={styles.faqQuestion}>{t('whatIsRoi')}</h3>
                <p style={styles.faqAnswer}>{t('roiExplanation')}</p>
                <div style={styles.formula}>ROI = ({t('profit')} ÷ {t('originalPrice')}) × 100</div>
              </div>
              <div style={styles.faqItem}>
                <h3 style={styles.faqQuestion}>{t('whatIsRoe')}</h3>
                <p style={styles.faqAnswer}>{t('roeExplanation')}</p>
                <div style={styles.formula}>ROE = ({t('profit')} ÷ {t('amountPaid')}) × 100</div>
              </div>
              <div style={styles.faqItem}>
                <h3 style={styles.faqQuestion}>{t('whyRoeHigher')}</h3>
                <p style={styles.faqAnswer}>{t('roeHigherExplanation')}</p>
              </div>
              <div style={styles.faqItem}>
                <h3 style={styles.faqQuestion}>{t('whatFeesIncluded')}</h3>
                <p style={styles.faqAnswer}>• 4% DLD (Dubai Land Department)<br/>• 2% {t('brokerCommission')}</p>
              </div>
              <div style={styles.faqItem}>
                <h3 style={styles.faqQuestion}>{t('howProfitCalculated')}</h3>
                <p style={styles.faqAnswer}>{t('profit')} = {t('grossNet')} - {t('paidAmount')}<br/><br/>{t('grossNet')} = {t('netToSeller')} - {t('totalFees')}<br/>{t('netToSeller')} = {t('sellingPrice')} - {t('remainingBalance')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ROICalculatorView;